import React from 'react';
import { MonoLabel } from '../Editorial';

/* Shared label/value rows and formatters for the school detail card.
   Ported from the `rowStack` + formatter block in
   ios/ProZpects/Views/Offers/MatchDetailSheet.swift, so the three platforms
   hide the same rows for the same null data. Every formatter returns null for
   null input — that null is what makes a row (and eventually a whole section)
   disappear. */

export const money = (n) => (n == null ? null : `$${Number(n).toLocaleString()}`);

export const pct = (n) => (n == null ? null : `${n}%`);

/** Percent already on a 0–100 scale as a float; drops a trailing ".0". */
export const pctD = (n) => {
  if (n == null) return null;
  return Number.isInteger(n) ? `${n}%` : `${Number(n).toFixed(1)}%`;
};

export const num = (n) => (n == null ? null : Number(n).toLocaleString());

export const yesno = (b) => (b == null ? null : (b ? 'Yes' : 'No'));

export const pretty = (s) => {
  if (s == null || s === '') return null;
  return String(s).replace(/[_-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

export const rng = (r, suffix = '') => (
  !r || (r.low == null && r.high == null) ? null : `${r.low ?? '?'}–${r.high ?? '?'}${suffix}`
);

/** "$1,200 / —" — null only when BOTH sides are missing. */
export const pair = (a, b) => ((a == null && b == null) ? null : `${a ?? '—'} / ${b ?? '—'}`);

/** True when at least one row has something to show. */
export const hasRows = (rows) => rows.some((r) => r.value != null && r.value !== '');

/** Label/value rows in a responsive grid; null-valued rows are dropped. */
export function Rows({ rows }) {
  const shown = rows.filter((r) => r.value != null && r.value !== '');
  if (shown.length === 0) return null;
  return (
    <div className="sd-rows">
      {shown.map((r) => (
        <div className="sd-row" key={r.label}>
          <span className="sd-row__label">{r.label}</span>
          <span className="sd-row__value">{r.value}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * A titled block inside an accordion. Renders nothing unless it has content —
 * the same `sectionWrap(title, hasContent:)` contract the apps use, which is
 * what keeps a sparse school looking identical on web and in the app.
 */
export function SubSection({ title, hasContent, children }) {
  if (!hasContent) return null;
  return (
    <div className="sd-sub">
      <div className="sd-sub__title"><MonoLabel>{title}</MonoLabel></div>
      {children}
    </div>
  );
}

/** SubSection whose only content is a set of plain rows. */
export function RowSection({ title, rows }) {
  return (
    <SubSection title={title} hasContent={hasRows(rows)}>
      <Rows rows={rows} />
    </SubSection>
  );
}
